import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useIntl } from 'react-intl';

import { getAlgoliaIndex } from '@jsmdg/algolia-helpers';
import { AlgoliaAnalyticsDataSource, getAlgoliaAnalyticsStorage } from '@jsmdg/browser-storage';
import { logToSentry, useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { sendAlgoliaObjectIdsClickedAfterSearchEvent } from '@jsmdg/tracking';
import { ObjectType } from '../../../shared/enums/objectType';
import { RecommendationType } from '../../../shared/enums/recommendationType';
import { searchConfig } from '../../../shared/searchConfig';
import {
    type FragmentContext,
    type Product,
    type RecommendationConfig,
} from '../../../shared/types';
import { TrackingCategory, TrackingListName } from '../../enums';
import { getAlgoliaAnalyticsRecommendationClickedEventName } from '../../helper/getAlgoliaAnalyticsEventName';
import { useInsightsClient } from '../../hooks/useInsightsClient';
import { relatedProductMessages as messages } from '../../i18n';
import { Headlines } from '../Headlines';
import { ProductSlider } from '../ProductSlider';

type RelatedProductsProps = {
    readonly recommendationConfig: RecommendationConfig;
    readonly lazyLoad: boolean;
};

const isQuotaExceededError = (e: Error): boolean => {
    return e.message?.toLowerCase().includes('quota');
};

const RelatedProducts = ({
    lazyLoad,
    recommendationConfig,
}: RelatedProductsProps): JSX.Element | null => {
    const { initialState, options } = recommendationConfig;

    const { algoliaConfig, locale, pageType, tenant } = useFragmentContext<FragmentContext>();
    const intl = useIntl();
    const insightsClient = useInsightsClient();

    const recommendations = initialState?.relatedProducts || [];
    const [virtualSlide, setVirtualSlide] = useState(false);

    const relatedProductsConfig = searchConfig[RecommendationType.RelatedProducts];
    const indexName =
        initialState?.indexName ||
        getAlgoliaIndex({
            environment: algoliaConfig.environment,
            locale,
            tenant,
            sorting: relatedProductsConfig?.sorting,
            objectType: ObjectType.Product,
        });

    useEffect(() => {
        flushSync(() => {
            setVirtualSlide(true);
        });
    }, []);

    const onProductTileClick = ({ productId, queryId }: Product, position: number): void => {
        const eventName = getAlgoliaAnalyticsRecommendationClickedEventName(pageType);

        if (!(insightsClient && eventName && queryId)) return;

        sendAlgoliaObjectIdsClickedAfterSearchEvent({
            insightsClient,
            eventName,
            indexName,
            objectIds: [productId],
            queryId,
            positions: [position],
        });

        const algoliaAnalyticsStorage = getAlgoliaAnalyticsStorage();

        try {
            algoliaAnalyticsStorage.add(productId, AlgoliaAnalyticsDataSource.PRODUCT_LIST, {
                indexName,
                queryId,
                timestamp: Date.now(),
            });
        } catch (error_) {
            const error = error_ as Error;
            if (isQuotaExceededError(error)) {
                logToSentry(
                    `${error.message}; AlgoliaAnalytics entries: ${
                        Object.keys(algoliaAnalyticsStorage.get()).length
                    }`,
                );
            }
        }
    };

    return (
        <>
            <Headlines headline={options?.headline} subHeadline={options?.subHeadline} />
            <ProductSlider
                productItems={recommendations}
                title={options?.productSliderTitle ?? intl.formatMessage(messages.relatedProducts)}
                trackingCategory={TrackingCategory.ProductList}
                trackingListName={TrackingListName.Recommendation}
                dataTestId="related-products-slider"
                lazyLoad={lazyLoad}
                onProductTileClick={onProductTileClick}
                showSkeletons={!recommendations.length}
                virtualSlide={virtualSlide}
            />
        </>
    );
};

export { RelatedProducts };
